import { useState, useEffect, useCallback } from 'react';
import { Shield, Ghost, Circle, Gavel } from 'lucide-react';
import { apiService, StaffMemberDTO, ConnectionDayDTO, SanctionDTO } from '@/services/api.service';
import { getRankDisplay, getRankWeight, hexToRgba } from '@/config/ranks';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import { GroupFetchError } from '@/components/supervision/GroupNotices';
import { formatPlayerName } from '@/utils/playerName';
import styles from './StaffPage.module.scss';

function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  return `${m}m`;
}

function relativeLastSeen(ts: number): string {
  if (!ts) return 'Jamais vu';
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "À l'instant";
  if (min < 60) return `il y a ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `il y a ${d}j`;
}

export function StaffPage() {
  const [staff, setStaff] = useState<StaffMemberDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<StaffMemberDTO | null>(null);
  // Erreur de lecture, affichée seulement sous un groupe de jeu (GroupFetchError).
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const res = await apiService.getStaffMembers();
      setLoadError(res.error ?? null);
      if (res.data) {
        const sorted = [...res.data.staff].sort((a, b) => {
          if (a.online !== b.online) return a.online ? -1 : 1;
          const wa = getRankWeight(a.rank, a.isPremium);
          const wb = getRankWeight(b.rank, b.isPremium);
          if (wb !== wa) return wb - wa;
          return formatPlayerName(a.name, a.displayName).localeCompare(formatPlayerName(b.name, b.displayName));
        });
        setStaff(sorted);
        setSelected((prev) => prev ?? sorted[0] ?? null);
      }
      setLoading(false);
    })();
  }, []);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Staff <GameGroupBadge size="md" /></h1>
        <p className={styles.subtitle}>
          Membres du staff et temps de connexion. {!loading && `${staff.length} membre(s) suivi(s).`}
        </p>
      </header>

      <GroupFetchError error={loadError} />

      <div className={styles.layout}>
        {/* Liste */}
        <aside className={styles.list}>
          <div className={styles.listHeader}>
            <Shield size={18} strokeWidth={2} />
            <span>Membres du staff</span>
            <span className={styles.count}>{staff.length}</span>
          </div>

          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <span>Chargement...</span>
            </div>
          ) : staff.length === 0 ? (
            <div className={styles.emptyState}>
              <Ghost size={40} strokeWidth={1.5} />
              <p>Aucun staff suivi pour l'instant.</p>
              <span className={styles.hint}>Le suivi démarre dès qu'un staff se connecte.</span>
            </div>
          ) : (
            <div className={styles.listScroll}>
              {staff.map((m) => (
                <StaffRow
                  key={m.uuid}
                  member={m}
                  selected={selected?.uuid === m.uuid}
                  onClick={() => setSelected(m)}
                />
              ))}
            </div>
          )}
        </aside>

        {/* Détail */}
        <section className={styles.detail}>
          {selected ? (
            <StaffDetail member={selected} />
          ) : (
            <div className={styles.detailEmpty}>
              <Shield size={48} strokeWidth={1.5} />
              <p>Sélectionne un membre du staff pour voir son activité.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// ==================== ROW ====================

function StaffRow({ member, selected, onClick }: { member: StaffMemberDTO; selected: boolean; onClick: () => void }) {
  const rank = getRankDisplay(member.rank);
  return (
    <button className={`${styles.row} ${selected ? styles.rowActive : ''}`} onClick={onClick}>
      <img
        src={`https://mc-heads.net/avatar/${encodeURIComponent(member.name)}/32`}
        alt=""
        className={styles.avatar}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = 'https://mc-heads.net/avatar/MHF_Steve/32';
        }}
      />
      <div className={styles.rowInfo}>
        <span className={styles.rowName}>
          <span>{formatPlayerName(member.name, member.displayName)}</span>
          <span className={styles.rankBadge} style={{ color: rank.color, backgroundColor: hexToRgba(rank.color, 0.15) }}>
            {rank.label}
          </span>
        </span>
        <span className={styles.rowSub}>
          {member.online ? (member.serverName || 'En ligne') : relativeLastSeen(member.lastSeen)}
        </span>
      </div>
      <Circle
        size={10}
        className={member.online ? styles.dotOnline : styles.dotOffline}
        fill="currentColor"
      />
    </button>
  );
}

// ==================== DETAIL ====================

function StaffDetail({ member }: { member: StaffMemberDTO }) {
  const [days, setDays] = useState<7 | 30>(7);
  const [data, setData] = useState<ConnectionDayDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getStaffConnections(member.uuid, days);
    if (res.data) {
      setData(res.data.days);
      setTotal(res.data.totalMinutes);
    } else {
      setData([]);
      setTotal(0);
    }
    setLoading(false);
  }, [member.uuid, days]);

  useEffect(() => {
    load();
  }, [load]);

  const rank = getRankDisplay(member.rank);

  return (
    <div className={styles.detailInner}>
      <div className={styles.detailHeader}>
        <img
          src={`https://mc-heads.net/avatar/${encodeURIComponent(member.name)}/48`}
          alt=""
          className={styles.detailAvatar}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = 'https://mc-heads.net/avatar/MHF_Steve/48';
          }}
        />
        <div className={styles.detailIdentity}>
          <span className={styles.detailName}>{formatPlayerName(member.name, member.displayName)}</span>
          <span className={styles.rankBadge} style={{ color: rank.color, backgroundColor: hexToRgba(rank.color, 0.15) }}>
            {rank.label}
          </span>
        </div>
        <span className={`${styles.statusBadge} ${member.online ? styles.statusOnline : styles.statusOffline}`}>
          {member.online ? `En ligne · ${member.serverName || '—'}` : relativeLastSeen(member.lastSeen)}
        </span>
      </div>

      <div className={styles.detailControls}>
        <div className={styles.rangeTabs}>
          <button className={days === 7 ? styles.rangeActive : ''} onClick={() => setDays(7)}>7 jours</button>
          <button className={days === 30 ? styles.rangeActive : ''} onClick={() => setDays(30)}>30 jours</button>
        </div>
        <div className={styles.totalTime}>
          <span className={styles.totalLabel}>Temps total ({days}j)</span>
          <span className={styles.totalValue}>{formatMinutes(total)}</span>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <span>Chargement...</span>
        </div>
      ) : total === 0 ? (
        <div className={styles.noData}>
          <p>Aucune connexion enregistrée sur cette période.</p>
          <span className={styles.hint}>Le suivi n'accumule les données qu'à partir de sa mise en service.</span>
        </div>
      ) : (
        <ConnectionChart days={data} range={days} />
      )}

      <SanctionsHistory uuid={member.uuid} name={member.name} />
    </div>
  );
}

// ==================== HISTORIQUE DES SANCTIONS ====================

function sanctionTypeMeta(type: number, typeName: string): { label: string; color: string } {
  switch (type) {
    case 0: return { label: typeName || 'Ban', color: '#DC2626' };
    case 1: return { label: typeName || 'Mute', color: '#EA580C' };
    case 2: return { label: typeName || 'Warn', color: '#CA8A04' };
    default: return { label: typeName || 'Sanction', color: '#71717A' };
  }
}

function formatSanctionDate(ts: number | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function SanctionsHistory({ uuid, name }: { uuid: string; name: string }) {
  const [items, setItems] = useState<SanctionDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService.getSanctionsByStaff(uuid, name, 0, 25).then((res) => {
      if (cancelled) return;
      if (res.data) {
        setItems(res.data.items);
        setTotal(res.data.total);
      } else {
        setItems([]);
        setTotal(0);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [uuid, name]);

  return (
    <div className={styles.sanctionsSection}>
      <div className={styles.sanctionsHeader}>
        <Gavel size={18} strokeWidth={2} />
        <span>Sanctions posées</span>
        {!loading && <span className={styles.count}>{total}</span>}
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <span>Chargement...</span>
        </div>
      ) : items.length === 0 ? (
        <div className={styles.noData}>
          <p>Aucune sanction posée par ce staff.</p>
        </div>
      ) : (
        <div className={styles.sanctionsList}>
          {items.map((s) => {
            const meta = sanctionTypeMeta(s.type, s.typeName);
            return (
              <div key={s.id} className={styles.sanctionRow}>
                <span
                  className={styles.sanctionType}
                  style={{ color: meta.color, backgroundColor: hexToRgba(meta.color, 0.15) }}
                >
                  {meta.label}
                </span>
                <div className={styles.sanctionInfo}>
                  <span className={`${styles.sanctionVictim} ${s.revoked ? styles.revoked : ''}`}>
                    {formatPlayerName(s.victimName, s.victimDisplayName)}
                    {s.revoked && <span className={styles.revokedTag}>révoquée</span>}
                  </span>
                  {s.description && <span className={styles.sanctionDesc}>{s.description}</span>}
                </div>
                <span className={styles.sanctionDate}>{formatSanctionDate(s.createAt)}</span>
              </div>
            );
          })}
          {total > items.length && (
            <span className={styles.moreHint}>+ {total - items.length} autre(s)…</span>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== CHART (barres) ====================

function formatDayLabel(dateStr: string): string {
  const [, m, d] = dateStr.split('-');
  return `${d}/${m}`;
}

function ConnectionChart({ days, range }: { days: ConnectionDayDTO[]; range: 7 | 30 }) {
  const max = Math.max(...days.map((d) => d.minutes), 1);
  // Sur 30j, on n'affiche qu'une étiquette sur ~5 pour ne pas surcharger.
  const labelEvery = range === 30 ? 5 : 1;

  return (
    <div className={styles.chart}>
      <div className={styles.bars}>
        {days.map((d, i) => (
          <div className={styles.barCol} key={d.date} title={`${formatDayLabel(d.date)} — ${formatMinutes(d.minutes)}`}>
            <div className={styles.barTrack}>
              <div
                className={styles.bar}
                style={{ height: `${(d.minutes / max) * 100}%` }}
              />
            </div>
            <span className={styles.barLabel}>{i % labelEvery === 0 ? formatDayLabel(d.date) : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
